// "Ask your mail" — natural-language Q&A over threads with progressive scope
// expansion. Default scope: the open thread (when launched from one) or the
// current inbox; a miss offers to widen to all accessible inboxes (and to
// chase a mentioned person across them).

import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Loader2, MailQuestion, Search, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Citation {
  threadId: number;
  n: number;
  subject: string;
  lastMessageAt: string;
}
interface SearchResult {
  found: boolean;
  answer: string;
  citations: Citation[];
  persons: { name: string; emails: string[] }[];
  participants: string[];
  scannedThreads: number;
  scannedMessages: number;
  candidateThreads: number;
  scopeLabel: string;
  tookMs: number;
}
type Scope = 'thread' | 'account' | 'all';

interface Turn {
  id: number;
  question: string;
  scope: Scope;
  scopeLabel: string;
  /** Explicit person filter carried by an expansion chip. */
  personEmails?: string[];
  loading: boolean;
  result?: SearchResult;
  error?: string;
}

/** "julian@x.com" → "julian" — friendly chip labels from addresses. */
function nameOf(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local.split(/[._-]/)[0] || local;
}

export interface AskMailPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-scope to a thread (opened from the reading pane). */
  threadId?: number | null;
  /** The currently selected inbox (null = viewing all). */
  accountId: number | null;
  accountLabel: string;
  onOpenThread: (threadId: number) => void;
}

let turnSeq = 1;

export function AskMailPanel({
  open,
  onOpenChange,
  threadId,
  accountId,
  accountLabel,
  onOpenThread,
}: AskMailPanelProps) {
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // The panel component stays mounted across opens; only the dialog content
  // unmounts. Reset the transcript whenever it reopens against a different
  // scope — thread A's answers must not appear labelled "in this thread"
  // while the input is scoped to thread B.
  const scopeKey = `${threadId ?? 'none'}:${accountId ?? 'none'}`;
  const lastScopeRef = useRef(scopeKey);
  useEffect(() => {
    if (open && lastScopeRef.current !== scopeKey) {
      setTurns([]);
      setQuestion('');
      lastScopeRef.current = scopeKey;
    }
  }, [open, scopeKey]);

  const defaultScope: Scope = threadId ? 'thread' : accountId != null ? 'account' : 'all';
  const defaultScopeLabel = threadId ? 'this thread' : accountId != null ? accountLabel : 'all your inboxes';

  async function ask(q: string, scope: Scope, personEmails?: string[]) {
    const id = turnSeq++;
    const personBit =
      personEmails && personEmails.length > 0
        ? ` · with ${personEmails.map(nameOf).join(', ')}`
        : '';
    const scopeLabel =
      (scope === 'thread' ? 'this thread' : scope === 'account' ? accountLabel : 'all your inboxes') +
      personBit;
    setTurns((prev) => [...prev, { id, question: q, scope, scopeLabel, personEmails, loading: true }]);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 50);
    try {
      const r = await fetch('/api/ai/search', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          question: q,
          scope,
          threadId: scope === 'thread' ? threadId : undefined,
          accountId: scope === 'account' ? accountId : undefined,
          personEmails,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error((j as { detail?: string; error?: string }).detail || (j as { error?: string }).error || `HTTP ${r.status}`);
      }
      const result = (await r.json()) as SearchResult;
      setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, loading: false, result } : t)));
    } catch (e) {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, loading: false, error: e instanceof Error ? e.message : 'Search failed' }
            : t,
        ),
      );
    }
    setTimeout(() => scrollRef.current?.scrollTo({ top: 999999, behavior: 'smooth' }), 100);
  }

  function submit() {
    const q = question.trim();
    if (q.length < 3) return;
    setQuestion('');
    void ask(q, defaultScope);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85dvh] flex-col gap-0 p-0 sm:max-w-xl">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="size-4 text-violet-500" strokeWidth={2} />
            Ask your mail
          </DialogTitle>
        </DialogHeader>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {turns.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <MailQuestion className="mx-auto mb-2 size-8 opacity-40" strokeWidth={1.5} />
              Ask anything about your mail — <br />
              <span className="italic">"Did we discuss how much the project would cost?"</span>
              <div className="mt-2 text-xs">Searching {defaultScopeLabel} first.</div>
            </div>
          )}
          <div className="space-y-4">
            {turns.map((t) => (
              <TurnView
                key={t.id}
                turn={t}
                onExpand={(scope, personEmails) => void ask(t.question, scope, personEmails)}
                onOpenThread={onOpenThread}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 border-t px-4 py-3">
          <input
            autoFocus
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            placeholder={`Ask about ${defaultScopeLabel}…`}
            className="h-9 flex-1 rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <Button size="icon" className="size-9" onClick={submit} disabled={question.trim().length < 3}>
            <ArrowUp className="size-4" strokeWidth={2} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Answer text with [n] citation tokens rendered as clickable links to their
 *  threads, and "- " list lines given a hanging indent for scanability. */
function AnswerText({
  answer,
  citations,
  onOpenThread,
}: {
  answer: string;
  citations: Citation[];
  onOpenThread: (threadId: number) => void;
}) {
  const byN = new Map(citations.map((c) => [c.n, c]));
  const renderLine = (line: string, key: number) => {
    const parts = line.split(/(\[\d+\])/g);
    const isListItem = /^\s*-\s/.test(line);
    return (
      <div key={key} className={isListItem ? 'pl-4 -indent-2' : undefined}>
        {parts.map((p, i) => {
          const m = p.match(/^\[(\d+)\]$/);
          const c = m ? byN.get(Number(m[1])) : undefined;
          if (c) {
            return (
              <button
                key={i}
                type="button"
                onClick={() => onOpenThread(c.threadId)}
                className="mx-0.5 rounded bg-violet-500/10 px-1 font-mono text-[11px] text-violet-700 hover:bg-violet-500/20 dark:text-violet-300"
                title={c.subject}
              >
                {p}
              </button>
            );
          }
          return <span key={i}>{p}</span>;
        })}
      </div>
    );
  };
  return (
    <div className="space-y-1 text-sm">
      {answer.split('\n').filter((l) => l.trim().length > 0).map(renderLine)}
    </div>
  );
}

function TurnView({
  turn,
  onExpand,
  onOpenThread,
}: {
  turn: Turn;
  onExpand: (scope: Scope, personEmails?: string[]) => void;
  onOpenThread: (threadId: number) => void;
}) {
  const r = turn.result;
  const persons = r?.persons ?? [];
  // Expansion is ALWAYS available (a found answer is often partial); a miss
  // just makes the widen button prominent instead of subtle.
  const offerWiden = r && turn.scope !== 'all';
  const emphasize = r && !r.found;
  const nextScope: Scope = turn.scope === 'thread' ? 'account' : 'all';
  // From a thread-scoped answer, also offer chasing the thread's correspondents
  // ("other messages by Julian") across the wider scope.
  const participants = turn.scope === 'thread' ? (r?.participants ?? []) : [];
  return (
    <div>
      <div className="flex items-start gap-2">
        <Search className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" strokeWidth={2} />
        <div className="min-w-0">
          <div className="text-sm font-medium">{turn.question}</div>
          <div className="text-[11px] text-muted-foreground">in {turn.scopeLabel}</div>
        </div>
      </div>

      <div className="mt-2 rounded-lg border bg-muted/20 px-3 py-2.5">
        {turn.loading && (
          <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" strokeWidth={2} />
            Searching {turn.scopeLabel}… {turn.scope !== 'thread' && 'this can take ~20 seconds'}
          </div>
        )}
        {turn.error && <p className="text-sm text-destructive">{turn.error}</p>}
        {r && (
          <>
            <AnswerText answer={r.answer} citations={r.citations} onOpenThread={onOpenThread} />
            {r.citations.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.citations.map((c) => (
                  <button
                    key={c.n}
                    type="button"
                    onClick={() => onOpenThread(c.threadId)}
                    className="inline-flex max-w-full items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px] hover:bg-accent"
                    title={c.subject}
                  >
                    <span className="font-mono text-muted-foreground">[{c.n}]</span>
                    <span className="truncate">{c.subject || '(no subject)'}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 text-[10px] text-muted-foreground">
              Searched {r.scannedThreads.toLocaleString()} threads
              {r.scannedMessages > 0 && ` (${r.scannedMessages.toLocaleString()} messages)`} in{' '}
              {(r.tookMs / 1000).toFixed(1)}s
            </div>
            {offerWiden && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button
                  size="xs"
                  variant={emphasize ? 'default' : 'outline'}
                  onClick={() => onExpand(nextScope, turn.personEmails)}
                >
                  {nextScope === 'account'
                    ? 'Search this whole inbox'
                    : persons.length > 0 || (turn.personEmails?.length ?? 0) > 0
                      ? `Search ${
                          turn.personEmails?.length
                            ? turn.personEmails.map(nameOf).join(', ')
                            : persons.map((p) => p.name).join(', ')
                        } across all your inboxes`
                      : 'Search all your inboxes'}
                </Button>
                {participants.length > 0 && (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => onExpand('account', participants)}
                  >
                    Only messages with {participants.map(nameOf).join(', ')} in this inbox
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
